import { useEffect, useRef, useState } from "react";
import type { TemplateResult, TemplateSettings } from "../lib/types";
import { effectiveCellPx } from "../lib/types";
import { renderTemplate } from "../lib/imageProcessing";
import { zoomedCanvasStyle, type ZoomLevel } from "./ZoomControls";
import "./InteractiveGrid.css";

interface Props {
  result: TemplateResult;
  settings: TemplateSettings;
  progress: ReadonlySet<number>;
  highlightIndex: number | null;
  zoom: ZoomLevel;
  onToggleCell: (cellIdx: number) => void;
}

const HEADER_PAD = { top: 48, left: 48 };

export default function InteractiveGrid({
  result,
  settings,
  progress,
  highlightIndex,
  zoom,
  onToggleCell,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [naturalW, setNaturalW] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!canvasRef.current) return;
    renderTemplate(canvasRef.current, result, settings, progress);
    setNaturalW(canvasRef.current.width);

    if (highlightIndex !== null) {
      const ctx = canvasRef.current.getContext("2d");
      if (!ctx) return;
      // Dim cells whose palette index is NOT highlightIndex.
      const { grid, indices } = result;
      const cellPx = effectiveCellPx(settings);
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      for (let row = 0; row < grid; row++) {
        for (let col = 0; col < grid; col++) {
          if (indices[row * grid + col] !== highlightIndex) {
            ctx.fillRect(
              HEADER_PAD.left + col * cellPx,
              HEADER_PAD.top + row * cellPx,
              cellPx,
              cellPx
            );
          }
        }
      }
    }
  }, [result, settings, progress, highlightIndex]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // getBoundingClientRect reflects the displayed (zoomed) size; scaling to the
    // canvas's intrinsic pixel coords keeps clicks accurate at any zoom level.
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const cellPx = effectiveCellPx(settings);
    const col = Math.floor((x - HEADER_PAD.left) / cellPx);
    const row = Math.floor((y - HEADER_PAD.top) / cellPx);
    if (row < 0 || row >= result.grid || col < 0 || col >= result.grid) return;
    onToggleCell(row * result.grid + col);
  };

  return (
    <>
      <div className="zoom-scroll-area">
        <canvas
          ref={canvasRef}
          className="interactive-canvas"
          style={zoomedCanvasStyle(zoom, naturalW)}
          onClick={handleClick}
        />
      </div>
      <p className="muted small">
        Tap or click any square to mark it as done. Tap again to undo.
      </p>
    </>
  );
}
